import { useEffect, useState } from "react";

import { applyMarketBiasGlobalScore, fetchEvents } from "../services/advisorService";
import { formatMarketBias, formatRiskLevel } from "../utils/marketLabels";

const MARKET_SUPPORT_OPTIONS = [
  { value: "NULLIFIED", label: "Nullified", range: "0 to <1", min: 0, max: 0.99, defaultScore: 0 },
  { value: "POSITIVE", label: "Positive", range: "1 to <2.5", min: 1, max: 2.49, defaultScore: 1 },
  { value: "NEUTRAL", label: "Neutral", range: "2.5 to <5", min: 2.5, max: 4.99, defaultScore: 2.5 },
  { value: "NEGATIVE", label: "Negative", range: "5 to <7.5", min: 5, max: 7.49, defaultScore: 5 },
  { value: "STRONG_HIT", label: "Strong Hit", range: "7.5 to 10", min: 7.5, max: 10, defaultScore: 7.5 },
  { value: "BEYOND_CONTROL", label: "Beyond Control", range: "above 10", min: 10.01, max: null, defaultScore: 10.01 },
];

function firstAvailable(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== "");
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatScore(value, suffix = "") {
  const resolved = firstAvailable(value);
  if (resolved === undefined) return "-";

  const numericValue = Number(resolved);
  if (!Number.isFinite(numericValue)) return resolved;

  return `${Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(2)}${suffix}`;
}

function formatMarketValue(value) {
  const resolved = firstAvailable(value);
  if (resolved === undefined) return null;

  const numericValue = Number(resolved);
  if (!Number.isFinite(numericValue)) return resolved;

  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
}

function sanitizeInputText(value) {
  if (!value) return value;

  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/\u00e2\u0080[\u0098\u0099]/g, "'")
    .replace(/\u00e2\u0080[\u009c\u009d]/g, "\"")
    .replace(/\u00e2\u0080[\u0093\u0094]/g, "-")
    .replace(/\u00e2\u0080\u00a6/g, "...")
    .replace(/\u00c2|\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeMatchKey(value) {
  return String(value ?? "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function getMarketSupportOption(value) {
  return MARKET_SUPPORT_OPTIONS.find((option) => option.value === value) ?? MARKET_SUPPORT_OPTIONS[2];
}

function getMarketSupportFromScore(score) {
  const number = toNumber(score);
  if (number === null) return MARKET_SUPPORT_OPTIONS[2];

  return MARKET_SUPPORT_OPTIONS.find((option) =>
    number >= option.min && (option.max === null || number <= option.max)
  ) ?? MARKET_SUPPORT_OPTIONS[MARKET_SUPPORT_OPTIONS.length - 1];
}

function averageSectionScore(rows = []) {
  const scores = rows
    .map((row) => toNumber(row.score))
    .filter((score) => score !== null);

  if (!scores.length) return null;

  return scores.reduce((total, score) => total + score, 0) / scores.length;
}

function formatMarketMove(row) {
  const label = row?.symbol ?? row?.name;
  const changePct = firstAvailable(row?.changePct);
  const previousClose = formatMarketValue(row?.previousClose);
  const currentPrice = formatMarketValue(row?.price);

  if (changePct !== undefined && previousClose && currentPrice) {
    return `${label}: prev ${previousClose}, current ${currentPrice}, change ${changePct}`;
  }

  if (changePct !== undefined) return `${label}: ${changePct}`;

  if (previousClose && currentPrice) return `${label}: prev ${previousClose}, current ${currentPrice}`;

  return `${label}: ${formatScore(row?.change, "")}`;
}

function findMarketRows(marketBias, names = []) {
  const normalizedNames = names.map((name) => String(name).toLowerCase());
  return (marketBias?.marketRows ?? []).filter((row) => {
    const rowText = `${row?.name ?? ""} ${row?.symbol ?? ""}`.toLowerCase();
    return normalizedNames.some((name) => rowText.includes(name));
  });
}

function buildScoreRow({ id, name, scope, score, note, grouped = false, sourceInput = null }) {
  const support = getMarketSupportFromScore(score);

  return {
    id,
    name,
    scope,
    support: support.value,
    score: firstAvailable(score, support.defaultScore),
    note,
    grouped,
    sourceInput,
  };
}

const NEWS_STOP_WORDS = new Set([
  "ABOUT", "AFTER", "AGAINST", "AMID", "AND", "ARE", "AS", "AT", "BE", "BEEN", "BEFORE", "BEING", "BETWEEN",
  "BY", "CAN", "COULD", "DAY", "DAYS", "FOR", "FROM", "HAS", "HAVE", "HOW", "IN", "INTO", "IS", "ITS",
  "LATEST", "LIVE", "MARKET", "MARKETS", "MAY", "MORE", "NEWS", "NEW", "NOT", "ON", "OVER", "REUTERS",
  "SAYS", "SHARE", "STOCK", "STOCKS", "THE", "THIS", "TO", "TODAY", "UPDATE", "WAS", "WERE", "WITH",
  "WILL", "WORLD",
]);

const NEWS_TOPIC_ALIASES = new Map([
  ["FED", "FEDERAL RESERVE"],
  ["FEDS", "FEDERAL RESERVE"],
  ["FEDERAL", "FEDERAL RESERVE"],
  ["TRUMP", "US"],
  ["UNITED", "US"],
  ["STATES", "US"],
  ["AMERICA", "US"],
  ["AMERICAN", "US"],
  ["U.S", "US"],
  ["U.S.", "US"],
  ["USA", "US"],
]);

const STRONG_NEWS_TOPICS = new Set([
  "CHINA", "FEDERAL RESERVE", "FED", "FII", "DII", "INDIA", "IRAN", "ISRAEL", "NIFTY", "OIL", "RBI",
  "RUSSIA", "SENSEX", "TARIFF", "UKRAINE", "US",
]);

const ETF_INDEX_NAMES = {
  QQQ: "Nasdaq 100",
  SPY: "S&P 500",
  DIA: "Dow Jones Industrial Average",
  IWM: "Russell 2000",
  EPI: "India Earnings Index",
};

function normalizeTopicToken(value) {
  const cleaned = String(value ?? "").replace(/[^A-Za-z0-9.]/g, "").toUpperCase();
  if (!cleaned || cleaned.length < 2 || NEWS_STOP_WORDS.has(cleaned)) return null;
  return NEWS_TOPIC_ALIASES.get(cleaned) ?? cleaned;
}

function extractNewsTopicTokens(input) {
  const text = [
    input.relatedEventName,
    input.relatedEventCode,
    input.symbol,
    input.headline,
    input.summary,
  ].filter(Boolean).join(" ");
  const rawTokens = text.split(/\s+/).map(normalizeTopicToken).filter(Boolean);
  const uniqueTokens = [...new Set(rawTokens)];
  const strongTokens = uniqueTokens.filter((token) => STRONG_NEWS_TOPICS.has(token));

  if (strongTokens.length) {
    return strongTokens.slice(0, 3).sort();
  }

  return uniqueTokens
    .filter((token) => token.length > 3)
    .slice(0, 2)
    .sort();
}

function buildNewsGroupKey(input, index) {
  if (input.relatedEventCode) {
    return `EVENT:${normalizeMatchKey(input.relatedEventCode)}`;
  }
  if (input.feedUrl) {
    const match = String(input.feedUrl).match(/[?&]q=([^&]+)/);
    if (match?.[1]) {
      const decodedQuery = decodeURIComponent(match[1].replace(/\+/g, " "));
      const queryTokens = decodedQuery.split(/\s+/).map(normalizeTopicToken).filter(Boolean);
      const strongQueryTokens = [...new Set(queryTokens.filter((token) => STRONG_NEWS_TOPICS.has(token)))].sort();
      if (strongQueryTokens.length) return `TOPIC:${strongQueryTokens.slice(0, 3).join("|")}`;
    }
  }
  const topicTokens = extractNewsTopicTokens(input);
  if (topicTokens.length) return `TOPIC:${topicTokens.join("|")}`;
  return `SINGLE:${input.inputId ?? input.sourceUrl ?? input.headline ?? index}`;
}

function formatNewsItem(input) {
  return {
    id: input.inputId ?? input.sourceUrl ?? input.headline,
    headline: sanitizeInputText(input.headline ?? "News item"),
    sourceName: sanitizeInputText(input.sourceName),
    publishedAt: input.publishedAt,
    sourceUrl: input.sourceUrl,
    summary: sanitizeInputText(input.summary),
  };
}

function formatNewsItemNote(item) {
  return [
    item.sourceName,
    item.publishedAt ? `Published ${item.publishedAt}` : null,
    item.summary,
  ].filter(Boolean).join(" | ");
}

function buildGroupedRawInputScoreRows(inputs = [], fallbackInputType = "NEWS") {
  const groups = new Map();

  inputs.forEach((input, index) => {
    const key = buildNewsGroupKey(input, index);
    if (!groups.has(key)) {
      groups.set(key, {
        id: `${fallbackInputType.toLowerCase()}-${key}-${index}`,
        input,
        items: [],
      });
    }
    groups.get(key).items.push(formatNewsItem(input));
  });

  return [...groups.values()].map((group, index) => {
    const firstItem = group.items[0];
    const relatedName = sanitizeInputText(group.input.relatedEventName);
    const topicName = relatedName || firstItem?.headline || "Market news";
    const row = buildScoreRow({
      id: group.id,
      name: group.items.length > 1 ? topicName : firstItem?.headline,
      scope: [
        group.input.region,
        group.input.category,
        group.input.relatedEventCode,
        group.items.length > 1 ? `${group.items.length} news items` : null,
      ].filter(Boolean).join(" / ") || group.input.inputType || "NEWS",
      score: group.input.adminScore,
      note: group.items.length > 1 ? null : formatNewsItemNote(firstItem),
      grouped: group.items.length > 1,
    });
    row.newsItems = group.items;
    row.newsCount = group.items.length;
    row.groupSortIndex = index;
    return row;
  });
}

function buildSingleRawInputScoreRow(inputs = [], fallbackInputType = "NEWS", panelName = "News") {
  const newsItems = inputs.map(formatNewsItem);
  if (!newsItems.length) return [];

  const firstInput = inputs[0] ?? {};
  const row = buildScoreRow({
    id: `${fallbackInputType.toLowerCase()}-single-panel`,
    name: panelName,
    scope: [
      firstInput.region,
      firstInput.category,
      `${newsItems.length} news items`,
    ].filter(Boolean).join(" / ") || fallbackInputType,
    score: firstInput.adminScore,
    note: null,
    grouped: true,
  });
  row.newsItems = newsItems;
  row.newsCount = newsItems.length;
  row.groupSortIndex = 0;
  return [row];
}

function formatQuoteInputNote(input) {
  const previousValue = formatMarketValue(input.previousValue);
  const currentValue = formatMarketValue(input.currentValue);
  const changeValue = formatMarketValue(input.changeValue);
  const changePercent = formatMarketValue(input.changePercent);

  return [
    sanitizeInputText(input.sourceName),
    previousValue ? `Prev ${previousValue}` : null,
    currentValue ? `Current ${currentValue}` : null,
    changeValue ? `Change ${changeValue}` : null,
    changePercent ? `Change % ${changePercent}%` : null,
    sanitizeInputText(input.summary),
  ].filter(Boolean).join(" | ");
}

function buildQuoteInputScoreRow(input, index, options = {}) {
  const { includeMarketClassification = true, includeEtfIndexName = false } = options;
  const normalizedSymbol = String(input.symbol ?? "").toUpperCase();
  const etfIndexName = includeEtfIndexName ? ETF_INDEX_NAMES[normalizedSymbol] : null;

  return buildScoreRow({
    id: input.inputId ?? `${input.inputType ?? "quote-input"}-${index}`,
    name: sanitizeInputText(input.headline ?? input.symbol ?? input.sourceCode ?? "Market quote"),
    scope: [
      includeMarketClassification ? input.region : null,
      includeMarketClassification ? input.category : null,
      input.symbol,
      etfIndexName,
      input.sourceCode,
    ].filter(Boolean).join(" / ") || input.inputType || "QUOTE",
    score: firstAvailable(input.adminScore, input.changePercent, input.changeValue),
    note: formatQuoteInputNote(input),
    sourceInput: input,
  });
}

function getActiveEventMatches(marketBias) {
  const activeEvents = [
    ...(Array.isArray(marketBias?.eventImpact?.activeEvents) ? marketBias.eventImpact.activeEvents : []),
    ...(Array.isArray(marketBias?.indiaBiasDetails?.activeEvents) ? marketBias.indiaBiasDetails.activeEvents : []),
  ];
  const factLines = [
    ...(Array.isArray(marketBias?.eventImpact?.facts) ? marketBias.eventImpact.facts : []),
    ...(Array.isArray(marketBias?.indiaBiasDetails?.facts) ? marketBias.indiaBiasDetails.facts : []),
  ];

  return { activeEvents, factLines };
}

function findEventNewsNote(event, marketBias) {
  const eventCode = normalizeMatchKey(event.eventCode ?? event.code);
  const eventName = normalizeMatchKey(event.eventName ?? event.name);
  const { activeEvents, factLines } = getActiveEventMatches(marketBias);
  const activeMatch = activeEvents.find((activeEvent) => {
    const activeCode = normalizeMatchKey(activeEvent.eventCode ?? activeEvent.code);
    const activeName = normalizeMatchKey(activeEvent.eventName ?? activeEvent.name);
    return (eventCode && activeCode === eventCode) || (eventName && activeName === eventName);
  });

  if (activeMatch?.matchedHeadline?.title) {
    const source = activeMatch.matchedHeadline.source ? `${activeMatch.matchedHeadline.source}: ` : "";
    const count = Number(activeMatch.matchCount);
    const countText = Number.isFinite(count) && count > 1 ? ` (${count} matches)` : "";
    return `${source}${activeMatch.matchedHeadline.title}${countText}`;
  }

  const factMatch = factLines.find((fact) => {
    const normalizedFact = normalizeMatchKey(fact);
    return (eventName && normalizedFact.includes(eventName)) || (eventCode && normalizedFact.includes(eventCode));
  });

  return factMatch || "No active matched news in latest batch.";
}

function buildMaintainedEventRows(events = [], marketBias) {
  return events.map((event, index) => buildScoreRow({
    id: `event-${event.eventId ?? event.id ?? event.eventCode ?? event.code ?? index}`,
    name: event.eventName ?? event.name ?? event.eventCode ?? event.code ?? "Maintained event",
    scope: event.region ?? "EVENT",
    score: firstAvailable(event.impactWeight, event.riskAdjustment, event.score),
    note: findEventNewsNote(event, marketBias),
  }));
}

function buildGlobalNewsRows(marketBias) {
  const globalEvents = Array.isArray(marketBias?.eventImpact?.activeEvents)
    ? marketBias.eventImpact.activeEvents
    : [];
  const globalFacts = Array.isArray(marketBias?.eventImpact?.facts)
    ? marketBias.eventImpact.facts
    : [];

  if (!globalEvents.length && !globalFacts.length) return [];

  const score = firstAvailable(
    marketBias?.eventImpact?.riskAdjustment,
    marketBias?.eventImpact?.riskScore,
    marketBias?.eventImpact?.score,
    globalEvents.reduce((total, event) => total + Number(firstAvailable(event.riskAdjustment, event.impactWeight, 0)), 0)
  );
  const globalNotes = globalEvents
    .map((event) => event.matchedHeadline?.title ?? event.eventName ?? event.eventCode)
    .filter(Boolean);

  return [
    buildScoreRow({
      id: "global-news",
      name: "Global News",
      scope: marketBias?.eventImpact?.scope ?? "GLOBAL",
      score,
      note: [...globalNotes, ...globalFacts].join(" | "),
      grouped: true,
    }),
  ];
}

function buildEtfRows(marketBias) {
  return findMarketRows(marketBias, ["sp500", "spy", "nasdaq", "qqq", "dow", "dia"])
    .map((row, index) => buildScoreRow({
      id: `etf-${row.symbol ?? row.name ?? index}`,
      name: row.symbol ?? row.name ?? "ETF",
      scope: "ETF",
      score: firstAvailable(row.changePct, row.change),
      note: formatMarketMove(row),
    }));
}

function buildCommodityRows(marketBias) {
  return findMarketRows(marketBias, ["gold", "gld", "crude", "uso", "uco", "copper", "cper"])
    .map((row, index) => buildScoreRow({
      id: `commodity-${row.symbol ?? row.name ?? index}`,
      name: row.symbol ?? row.name ?? "Commodity",
      scope: "COMMODITY",
      score: firstAvailable(row.changePct, row.change),
      note: formatMarketMove(row),
    }));
}

function buildMarketBiasScoreSections(marketBias, maintainedEvents = []) {
  return [
    { id: "maintained-events", title: "Maintained Events", rows: buildMaintainedEventRows(maintainedEvents, marketBias) },
    { id: "global-news", title: "Global News", rows: buildGlobalNewsRows(marketBias) },
    { id: "etf-items", title: "ETF Items", rows: buildEtfRows(marketBias) },
    { id: "commodities", title: "Commodities", rows: buildCommodityRows(marketBias) },
  ];
}

function buildMarketBiasInputSections(marketBiasInputs, marketBias, maintainedEvents = []) {
  if (!marketBiasInputs) {
    return buildMarketBiasScoreSections(marketBias, maintainedEvents);
  }

  if (marketBiasInputs.status === "APPLIED") {
    return buildAppliedMarketBiasSections(marketBiasInputs);
  }

  return [
    {
      id: "maintained-event-news",
      title: "Maintained Event News",
      rows: buildGroupedRawInputScoreRows(marketBiasInputs.maintainedEventNews ?? [], "MAINTAINED_EVENT_NEWS"),
    },
    {
      id: "global-news",
      title: "Global News",
      rows: buildSingleRawInputScoreRow(marketBiasInputs.globalNews ?? [], "GLOBAL_NEWS", "Global News"),
    },
    {
      id: "india-news",
      title: "India News",
      rows: buildSingleRawInputScoreRow(marketBiasInputs.indiaNews ?? [], "INDIA_NEWS", "India News"),
    },
    {
      id: "etf-inputs",
      title: "ETF Inputs",
      rows: (marketBiasInputs.etfInputs ?? []).map((input, index) =>
        buildQuoteInputScoreRow(input, index, {
          includeMarketClassification: false,
          includeEtfIndexName: true,
        })
      ),
    },
    {
      id: "commodity-inputs",
      title: "Commodity Inputs",
      rows: (marketBiasInputs.commodityInputs ?? []).map((input, index) =>
        buildQuoteInputScoreRow(input, index, { includeMarketClassification: false })
      ),
    },
  ];
}

function buildAppliedInputScoreRows(inputs = []) {
  return inputs.map((input, index) => {
    const row = buildScoreRow({
      id: input.inputId ?? `applied-input-${index}`,
      name: sanitizeInputText(input.headline ?? input.relatedEventName ?? input.symbol ?? "Saved market input"),
      scope: input.displayScope ?? [
        input.category,
        input.inputType,
        input.newsCount ? `${input.newsCount} news items` : null,
      ].filter(Boolean).join(" / "),
      score: input.adminScore,
      note: input.adminNotes ?? input.summary,
      grouped: Boolean(input.grouped) || (Array.isArray(input.newsItems) && input.newsItems.length > 1),
      sourceInput: input,
    });
    row.newsItems = Array.isArray(input.newsItems) ? input.newsItems : [];
    row.newsCount = input.newsCount ?? row.newsItems.length;
    return row;
  });
}

function buildAppliedMarketBiasSections(marketBiasInputs) {
  return [
    {
      id: "maintained-event-news",
      title: "Maintained Event News",
      rows: buildAppliedInputScoreRows(marketBiasInputs.maintainedEventNews ?? []),
    },
    {
      id: "global-news",
      title: "Global News",
      rows: buildAppliedInputScoreRows(marketBiasInputs.globalNews ?? []),
    },
    {
      id: "india-news",
      title: "India News",
      rows: buildAppliedInputScoreRows(marketBiasInputs.indiaNews ?? []),
    },
    {
      id: "etf-inputs",
      title: "ETF Inputs",
      rows: buildAppliedInputScoreRows(marketBiasInputs.etfInputs ?? []),
    },
    {
      id: "commodity-inputs",
      title: "Commodity Inputs",
      rows: buildAppliedInputScoreRows(marketBiasInputs.commodityInputs ?? []),
    },
  ];
}

function isMarketInstrumentSection(section) {
  return section.id === "etf-inputs" || section.id === "commodity-inputs" ||
    section.id === "etf-items" || section.id === "commodities";
}

function isMaintainedEventSection(section) {
  return section.id === "maintained-event-news" || section.id === "maintained-events";
}

function resolvePayloadScore(section, row) {
  if (!isMarketInstrumentSection(section)) return toNumber(row.score);

  const averageScore = averageSectionScore(section.rows);
  return averageScore === null ? toNumber(row.score) : averageScore;
}

function resolvePayloadSupport(section, row) {
  if (!isMarketInstrumentSection(section)) return row.support;

  return getMarketSupportFromScore(resolvePayloadScore(section, row)).value;
}

function toPayloadRows(scoreSections, sectionId, inputType) {
  const section = scoreSections.find((item) => item.id === sectionId);
  return (section?.rows ?? []).map((row) => {
    const input = row.sourceInput ?? {};

    return {
      ...input,
      inputId: row.id,
      inputType: input.inputType ?? inputType,
      sourceName: input.sourceName,
      sourceUrl: input.sourceUrl,
      feedUrl: input.feedUrl,
      sourceCode: input.sourceCode,
      headline: row.name,
      summary: row.note,
      relatedEventCode: input.relatedEventCode,
      relatedEventName: sectionId === "maintained-event-news" ? row.name : input.relatedEventName,
      region: input.region,
      category: input.category ?? section?.title,
      symbol: input.symbol,
      previousValue: input.previousValue,
      currentValue: input.currentValue,
      changeValue: input.changeValue,
      changePercent: input.changePercent,
      impactBias: input.impactBias,
      impactWeight: input.impactWeight,
      adminMarketSupport: resolvePayloadSupport(section, row),
      adminScore: resolvePayloadScore(section, row),
      adminNotes: row.note,
      displayScope: row.scope,
      grouped: Boolean(row.grouped),
      newsCount: row.newsCount ?? row.newsItems?.length ?? input.newsCount ?? null,
      newsItems: row.newsItems ?? input.newsItems ?? [],
      editable: true,
    };
  });
}

function buildApplyRequest(marketBiasInputs, scoreSections) {
  return {
    tradeDate: marketBiasInputs?.tradeDate,
    orchestratorRunId: marketBiasInputs?.orchestratorRunId,
    appliedBy: "admin",
    maintainedEventNews: toPayloadRows(scoreSections, "maintained-event-news", "MAINTAINED_EVENT_NEWS"),
    globalNews: toPayloadRows(scoreSections, "global-news", "GLOBAL_NEWS"),
    indiaNews: toPayloadRows(scoreSections, "india-news", "INDIA_NEWS"),
    etfInputs: toPayloadRows(scoreSections, "etf-inputs", "ETF_INPUT"),
    commodityInputs: toPayloadRows(scoreSections, "commodity-inputs", "COMMODITY_INPUT"),
  };
}

function buildScoreSnapshot(marketBiasInputs, scoreSections) {
  return JSON.stringify({
    tradeDate: marketBiasInputs?.tradeDate ?? null,
    orchestratorRunId: marketBiasInputs?.orchestratorRunId ?? null,
    sections: scoreSections.map((section) => ({
      id: section.id,
      rows: section.rows.map((row) => ({
        id: row.id,
        support: row.support,
        score: row.score,
        note: row.note,
      })),
    })),
  });
}

function hasRenderableNewsItems(row) {
  if (!Array.isArray(row.newsItems) || row.newsItems.length === 0) return false;
  if (row.newsItems.length > 1) return true;

  return normalizeMatchKey(row.newsItems[0]?.headline) !== normalizeMatchKey(row.name);
}

export default function MarketBiasCalculatorPage({ marketBias, marketBiasInputs, onRefreshMarketInputs, refreshing, onMarketBiasApplied }) {
  const [maintainedEvents, setMaintainedEvents] = useState([]);
  const [scoreSections, setScoreSections] = useState(() => buildMarketBiasInputSections(marketBiasInputs, marketBias, []));
  const [baselineSnapshot, setBaselineSnapshot] = useState("");
  const [applyingScore, setApplyingScore] = useState(false);
  const [scoreSummary, setScoreSummary] = useState(null);
  const [applyError, setApplyError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchEvents()
      .then((events) => {
        if (!cancelled) setMaintainedEvents(events);
      })
      .catch((err) => {
        console.warn("Unable to load maintained events for Market Bias Calculator.", err);
        if (!cancelled) setMaintainedEvents([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const nextSections = buildMarketBiasInputSections(marketBiasInputs, marketBias, maintainedEvents);
    setScoreSections(nextSections);
    setScoreSummary(marketBiasInputs?.summary ?? null);
    setBaselineSnapshot(marketBiasInputs?.status === "APPLIED"
      ? buildScoreSnapshot(marketBiasInputs, nextSections)
      : "");
  }, [marketBiasInputs, marketBias, maintainedEvents]);

  const updateEventSupport = (eventId, supportValue) => {
    const support = getMarketSupportOption(supportValue);
    setScoreSections((current) =>
      current.map((section) => ({
        ...section,
        rows: section.rows.map((row) =>
          row.id === eventId
            ? { ...row, support: support.value, score: support.defaultScore }
            : row
        ),
      }))
    );
  };

  const updateEventScore = (eventId, score) => {
    setScoreSections((current) =>
      current.map((section) => ({
        ...section,
        rows: section.rows.map((row) =>
          row.id === eventId ? { ...row, score } : row
        ),
      }))
    );
  };

  const totalScoreRows = scoreSections.reduce((total, section) => total + section.rows.length, 0);
  const currentSnapshot = buildScoreSnapshot(marketBiasInputs, scoreSections);
  const hasScoreChanges = !baselineSnapshot || currentSnapshot !== baselineSnapshot;
  const newsSections = scoreSections.filter((section) => !isMarketInstrumentSection(section));
  const marketInstrumentSections = scoreSections.filter(isMarketInstrumentSection);
  const actionLoading = refreshing || applyingScore;

  const requestRefreshMarketInputs = () => {
    setConfirmation({
      title: "Refresh market inputs?",
      message: "Latest market inputs will be loaded. Unsaved calculator changes will be replaced when you continue.",
      confirmLabel: "Refresh Inputs",
      onConfirm: async () => {
        setConfirmation(null);
        await onRefreshMarketInputs?.();
      },
    });
  };

  const applyGlobalScore = async () => {
    if (!hasScoreChanges) return;

    setApplyingScore(true);
    setApplyError("");

    try {
      const appliedState = await applyMarketBiasGlobalScore(buildApplyRequest(marketBiasInputs, scoreSections));
      const summary = appliedState?.summary ?? appliedState;
      setScoreSummary(summary);
      setBaselineSnapshot(currentSnapshot);
      onMarketBiasApplied?.(appliedState);
    } catch (err) {
      console.error("Unable to apply market bias score.", err);
      setApplyError("Unable to calculate market bias score.");
    } finally {
      setApplyingScore(false);
    }
  };

  const requestApplyGlobalScore = () => {
    if (!hasScoreChanges || !totalScoreRows) return;

    setConfirmation({
      title: "Apply market score?",
      message: "This saves the current scores and updates the market bias, risk, direction, and selected strategy used by the desk.",
      confirmLabel: "Apply Score",
      onConfirm: async () => {
        setConfirmation(null);
        await applyGlobalScore();
      },
    });
  };

  const renderAverageHeaderControls = (section) => {
    const averageScore = averageSectionScore(section.rows);
    const support = getMarketSupportFromScore(averageScore);

    return (
      <div className="event-score-average-controls">
        <select
          aria-label={`${section.title} average market support`}
          disabled
          value={support.value}
        >
          {MARKET_SUPPORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          aria-label={`${section.title} average market support score`}
          readOnly
          type="number"
          value={averageScore === null ? "" : averageScore.toFixed(2)}
        />
      </div>
    );
  };

  const renderMarketInstrumentSection = (section) => {
    const averageScore = averageSectionScore(section.rows);
    const averageSupport = getMarketSupportFromScore(averageScore);

    return (
      <div className="event-score-section" key={section.id}>
        <div className="event-score-section-title">
          <span>{section.title}</span>
          <strong>{section.rows.length}</strong>
        </div>
        {section.rows.length ? (
          <>
            {section.rows.map((eventRow) => {
              const support = getMarketSupportOption(eventRow.support);
              const numericScore = toNumber(eventRow.score);
              const outOfRange = numericScore === null ||
                numericScore < support.min ||
                (support.max !== null && numericScore > support.max);

              return (
                <div className={`event-score-line${outOfRange ? " is-invalid" : ""}`} key={eventRow.id}>
                  <div className="event-score-name">
                    <strong>{eventRow.name}</strong>
                    <span>{eventRow.scope}</span>
                  </div>
                  <select
                    aria-label={`${eventRow.name} market support`}
                    value={eventRow.support}
                    onChange={(event) => updateEventSupport(eventRow.id, event.target.value)}
                  >
                    {MARKET_SUPPORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={`${eventRow.name} market support score`}
                    max={support.max ?? undefined}
                    min={support.min}
                    step="0.01"
                    type="number"
                    value={eventRow.score}
                    onChange={(event) => updateEventScore(eventRow.id, event.target.value)}
                  />
                  <em>{support.range}</em>
                  {eventRow.note && <p>{eventRow.note}</p>}
                </div>
              );
            })}
            <div className="event-score-line event-score-summary-line">
              <div className="event-score-name">
                <strong>Average Score</strong>
                <span>{section.title}</span>
              </div>
              <select
                aria-label={`${section.title} average market support`}
                disabled
                value={averageSupport.value}
              >
                {MARKET_SUPPORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                aria-label={`${section.title} average market support score`}
                readOnly
                type="number"
                value={averageScore === null ? "" : averageScore.toFixed(2)}
              />
              <em>{averageSupport.range}</em>
            </div>
          </>
        ) : (
          <p className="event-score-empty">No rows available.</p>
        )}
      </div>
    );
  };

  const renderScoreSection = (section) => {
    if (isMarketInstrumentSection(section)) return renderMarketInstrumentSection(section);

    return (
      <div className="event-score-section" key={section.id}>
        <div className="event-score-section-title">
          <div className="event-score-section-title-main">
            <span>{section.title}</span>
            <strong>{section.rows.length}</strong>
          </div>
          {isMaintainedEventSection(section) && section.rows.length > 0 && renderAverageHeaderControls(section)}
        </div>
        {section.rows.length ? (
          section.rows.map((eventRow) => {
            const support = getMarketSupportOption(eventRow.support);
            const numericScore = toNumber(eventRow.score);
            const outOfRange = numericScore === null ||
              numericScore < support.min ||
              (support.max !== null && numericScore > support.max);
            const headlineSourceUrl = eventRow.newsItems?.length === 1
              ? eventRow.newsItems[0]?.sourceUrl
              : eventRow.sourceInput?.sourceUrl;

            return (
              <div className={`event-score-line${eventRow.grouped ? " is-grouped" : ""}${outOfRange ? " is-invalid" : ""}`} key={eventRow.id}>
                <div className="event-score-name">
                  {headlineSourceUrl ? (
                    <a className="event-score-headline-link" href={headlineSourceUrl} rel="noreferrer" target="_blank" title={eventRow.name}>
                      {eventRow.name}
                    </a>
                  ) : (
                    <strong title={eventRow.name}>{eventRow.name}</strong>
                  )}
                  <span>{eventRow.scope}</span>
                </div>
                <select
                  aria-label={`${eventRow.name} market support`}
                  value={eventRow.support}
                  onChange={(event) => updateEventSupport(eventRow.id, event.target.value)}
                >
                  {MARKET_SUPPORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={`${eventRow.name} market support score`}
                  max={support.max ?? undefined}
                  min={support.min}
                  step="0.01"
                  type="number"
                  value={eventRow.score}
                  onChange={(event) => updateEventScore(eventRow.id, event.target.value)}
                />
                <em>{support.range}</em>
                {eventRow.note && <p>{eventRow.note}</p>}
                {hasRenderableNewsItems(eventRow) && (
                  <ul className="event-score-news-list">
                    {eventRow.newsItems.map((item, index) => (
                      <li key={`${eventRow.id}-${item.id ?? index}`}>
                        {item.sourceUrl ? (
                          <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                            {item.headline}
                          </a>
                        ) : (
                          <span>{item.headline}</span>
                        )}
                        <small>{formatNewsItemNote(item)}</small>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        ) : (
          <p className="event-score-empty">No rows available.</p>
        )}
      </div>
    );
  };

  return (
    <section className="event-config-page market-bias-calculator-page">
      {actionLoading && (
        <div className="logic-loading-mask market-bias-action-mask" role="status" aria-live="polite">
          <div className="logic-loading-panel">
            <span className="logic-loading-spinner" aria-hidden="true" />
            <strong>{refreshing ? "Refreshing market inputs" : "Applying market score"}</strong>
            <p>
              {refreshing
                ? "Loading the latest maintained events, news, ETF, and commodity inputs for scoring."
                : "Saving the score and updating market bias, risk, direction, and strategy state."}
            </p>
          </div>
        </div>
      )}

      {confirmation && (
        <div className="market-confirmation-backdrop" role="presentation">
          <div
            aria-labelledby="market-confirmation-title"
            aria-modal="true"
            className="market-confirmation-dialog"
            role="dialog"
          >
            <div className="market-confirmation-icon" aria-hidden="true">!</div>
            <div className="market-confirmation-copy">
              <h3 id="market-confirmation-title">{confirmation.title}</h3>
              <p>{confirmation.message}</p>
            </div>
            <div className="market-confirmation-actions">
              <button
                className="market-confirmation-secondary"
                type="button"
                onClick={() => setConfirmation(null)}
              >
                Cancel
              </button>
              <button
                className="market-confirmation-primary"
                type="button"
                onClick={confirmation.onConfirm}
              >
                {confirmation.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="section-heading">
        <div>
          <h2>Market Bias Calculator</h2>
          <p>Manually score the daily market inputs before deriving strategy logic.</p>
          {marketBiasInputs?.orchestratorRunId && (
            <p className="market-input-refresh-meta">
              Loaded {marketBiasInputs.status} from {marketBiasInputs.orchestratorRunId}
            </p>
          )}
        </div>
        <div className="market-input-actions">
          <button
            className="refresh-btn"
            type="button"
            onClick={requestRefreshMarketInputs}
            disabled={actionLoading}
          >
            {refreshing ? "Refreshing Inputs" : "Refresh Market Inputs"}
          </button>
          <button
            className="refresh-btn"
            type="button"
            onClick={requestApplyGlobalScore}
            disabled={!totalScoreRows || !hasScoreChanges || actionLoading}
          >
            {applyingScore ? "Applying Score" : "Apply Market Score"}
          </button>
        </div>
      </div>

      {(scoreSummary || applyError) && (
        <div className="market-score-summary">
          {applyError ? (
            <p>{applyError}</p>
          ) : (
            <>
              <div><span>Global Score</span><strong>{formatScore(scoreSummary.globalScore)}</strong></div>
              <div><span>Bias</span><strong>{formatMarketBias(scoreSummary.marketBias)}</strong></div>
              <div><span>Risk</span><strong>{formatRiskLevel(scoreSummary.riskLevel)}</strong></div>
              <div><span>Direction</span><strong>{scoreSummary.strategyDirection}</strong></div>
              <div><span>Strategy</span><strong>{scoreSummary.selectedStrategyName}</strong></div>
            </>
          )}
        </div>
      )}

      <div className="signal-workflow-section">
        <div className="signal-workflow-heading">
          <span>01</span>
          <strong>Market Bias</strong>
        </div>
        <div className="event-score-lines" aria-label="Event market support scores">
          <div className="event-score-lines-title">
            <span>Input Groups</span>
            <strong>Market Support Score</strong>
          </div>
          {totalScoreRows ? (
            <>
              <div className="event-score-news-stack">
                {newsSections.map(renderScoreSection)}
              </div>
              <div className="event-score-market-grid">
                {marketInstrumentSections.map(renderScoreSection)}
              </div>
            </>
          ) : (
            <p className="event-score-empty">No maintained events, global news, ETF rows, or commodity rows available.</p>
          )}
        </div>
      </div>
    </section>
  );
}
